/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card, Textarea, Typography } from '@airbus/components-react';
import { useEffect, useState, useRef } from 'react';
import './CheckerFinEditComponent.scss';
import CheckerFinStatusComponent from './CheckerFinStatusComponent';
import CheckerFinEditHistoryComponent from './CheckerFinEditHistoryComponent';
import { COMMENTS } from '../../utils/constants';
import { handleOnchangeComment } from './CheckerFinEditModelUtils';
import { useAppSelector } from '../../store/hooksTypes';

// --- ULTRA-FAST DIRECT ELEMENT WRAPPER ---
// This completely isolates keyboard inputs from the React re-render cycles
const FastCommentTextarea = ({ value, onChange, className }: any) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Keeps the DOM text in sync with background updates (like Apply All actions)
  useEffect(() => {
    if (textareaRef.current && textareaRef.current.value !== value) {
      textareaRef.current.value = value || '';
    }
  }, [value]);

  return (
    <Textarea
      ref={textareaRef}
      className={className}
      rows={1}
      data-testid="commend-id"
      placeholder="Enter comment..."
      maxLength={1000}
      // Fires your exact logic instantly without blocking the native browser typing buffer
      onChange={(event) => {
        onChange(event.target.value);
      }}
      onPointerEnterCapture={undefined}
      onPointerLeaveCapture={undefined}
    />
  );
};

const CheckerFinInlineModelComponent = ({
  selectedFin,
  finStatusHistory,
  checkerDataTableState,
  CheckerFinEditModalPopupDispatcher,
  isError,
  commentCount,
  applyAllToggleState,
}: any) => {
  // Local state for immediate typing responsiveness
  const [commentText, setCommentText] = useState<any[]>(selectedFin);

  const commentFinState = useAppSelector((state) => state.displayCheckerDetailsTable.commentList);
  const featureToggleConfig = useAppSelector((state) => state.featureToggleDetails?.data);
  const featureToggle = featureToggleConfig?.airnavx_no_fin === 'true';

  // --- SYNC FROM REDUX ---
  useEffect(() => {
    const updatedComments = selectedFin.map((original: any) => {
      const changeEntry = checkerDataTableState.finChangesState.find(
        (c: any) => c.fleet_fin === original.fleet_fin && c.id_aircraft_registration === original.id_aircraft_registration,
      );
      return { ...original, ...changeEntry };
    });
    setCommentText(updatedComments);
  }, [checkerDataTableState.finChangesState]);

  const onCommentChange = (nextValue: string, index: number, finValue: any) => {
    // 1. Update local state immediately so UI is snappy
    const updatedLocal = [...commentText];
    if (applyAllToggleState) {
      updatedLocal.forEach((item, i) => {
        updatedLocal[i] = { ...item, comment: nextValue };
      });
    } else {
      updatedLocal[index] = { ...updatedLocal[index], comment: nextValue };
    }
    setCommentText(updatedLocal);

    // 2. Update Redux (this is what the Toggle in the parent relies on)
    const handleChangeCmdArgs = {
      commentText: updatedLocal,
      setCommentText,
      checkerDataTableState,
      CheckerFinEditModalPopupDispatcher,
    };

    handleOnchangeComment(
      nextValue,
      index,
      commentFinState,
      finValue,
      checkerDataTableState,
      handleChangeCmdArgs,
    );
  };

  return (
    <div className="modal-container-outer">
      {selectedFin.map((selectedFinValues: any, index: number) => {
        const currentComment = commentText[index]?.comment || '';
        const textareaClass = isError && commentCount > 0 && !currentComment ? 'text-box-error' : 'text-box-initial';

        return (
          <Card
            className="modal-container-card"
            key={`${selectedFinValues.id_aircraft_registration}__SEP__${selectedFinValues.fleet_fin}`}
          >
            <div className="modal-content">
              <div className="status-section">
                <CheckerFinStatusComponent
                  statusProps={selectedFinValues}
                  checkerDataTableState={checkerDataTableState}
                  CheckerFinEditModalPopupDispatcher={CheckerFinEditModalPopupDispatcher}
                  featureToggle={featureToggle}
                  applyAllToggleState={applyAllToggleState}
                  selectedFin={selectedFin}
                />
              </div>

              <div className="commends-section">
                <Typography className="comments-header-label">
                  {COMMENTS}
                  {' '}
                  <span className="required-comments-fields"> *</span>
                </Typography>

                {/* Handled via the Fast DOM-Ref layout to completely eliminate character dropping */}
                <FastCommentTextarea
                  value={currentComment}
                  className={textareaClass}
                  onChange={(nextValue: string) => {
                    onCommentChange(nextValue, index, selectedFinValues);
                  }}
                />
              </div>

              <div className="history-section">
                <CheckerFinEditHistoryComponent
                  finStatusHistory={finStatusHistory?.data?.[`${selectedFinValues.id_aircraft_registration}__SEP__${selectedFinValues.fleet_fin}`]}
                  finIndex={index}
                  checkerDataTableState={checkerDataTableState}
                />
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default CheckerFinInlineModelComponent;
